// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as TimelineActions from "features/timeline/actions"
import * as UserContextSlice from "../../user-context/slice"
import * as UserContextActions from "../../user-context/actions"
import { ListTagsByCategoryResponse, ListTagsByCategoryRequestSchema } from "generated/legebill/site/tag/v4/tag_pb"
import { throttleSame } from "../../../lib/rxutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executor, Init, isConnectError, tagService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"

type ListTagsByCategoryRpc = (request: Init<typeof ListTagsByCategoryRequestSchema>) => Observable<ListTagsByCategoryResponse>

export const listTagsByCategory = (listTagsByCategoryRpc: ListTagsByCategoryRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listTagsByCategory.match),
      throttleSame(2000, ({ payload: a }, { payload: b }) => a.tagCategoryId === b.tagCategoryId),
      mergeMap(({ payload }) =>
        of({ tagCategoryId: payload.tagCategoryId })
          .pipe(
            mergeMap(listTagsByCategoryRpc),
            map(response => Slice.setTags(response.tags)),
            catchError(e => handleError(e, payload.swallowFailure)),
          )
      ),
    )

const handleError = (error: unknown, swallowFailure: boolean | undefined): Observable<Action> => {
  if (isConnectError(error)) {
    const { code } = error
    if (code === Code.NotFound) {
      return concat(
        of(TimelineActions.onTimelineOutOfSync()),
        of(showSnackBar({
          message: t({
            id: "msg_list_tags_by_category_not_found",
            message: "Tag category not found.",
          }),
          messageType: "ERROR",
        }))
      )
    }
    if (code === Code.PermissionDenied) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(UserContextSlice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }

  return swallowFailure ?
    EMPTY :
    of(showSnackBar({
      message: t({
        id: "msg_list_tags_by_category_failed",
        message: "Failed to load tags for category. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      }),
      messageType: "ERROR",
    }))
}

const listTagsByCategoryRpc = (request: Init<typeof ListTagsByCategoryRequestSchema>): Observable<ListTagsByCategoryResponse> =>
  executor(() => tagService.listTagsByCategory(create(ListTagsByCategoryRequestSchema, request)))

export default listTagsByCategory(listTagsByCategoryRpc)
